import { useState, useCallback, useMemo } from 'react';
import { PaginationState } from 'src/components/common/TablePagination';

interface UsePaginationStateProps {
  initialPageSize?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

interface UsePaginationStateReturn {
  pageSize: number;
  currentPage: number;
  searchQuery: string;
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';

  // Actions
  setPageSize: (size: number) => void;
  setSearchQuery: (query: string) => void;
  setSorting: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToFirstPage: () => void;
  goToPage: (page: number) => void;
  reset: () => void;

  // Computed state for pagination component
  paginationState: PaginationState;

  // Offset management
  getCurrentOffset: () => number;
  setHasNextPage: (hasNext: boolean) => void;
  setPaginationInfo: (info: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
  }) => void;
}

/**
 * Custom hook for managing offset-based pagination state.
 * Handles page navigation, search, and sorting for efficient pagination.
 */
export const usePaginationState = ({
  initialPageSize = 20,
  onPageChange,
  onPageSizeChange,
}: UsePaginationStateProps = {}): UsePaginationStateReturn => {
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQueryState] = useState('');
  const [sortBy, setSortByState] = useState('name');
  const [sortDirection, setSortDirectionState] = useState<'ASC' | 'DESC'>(
    'ASC',
  );
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size);
      setCurrentPage(1);
      onPageSizeChange?.(size);
      onPageChange?.(1);
    },
    [onPageChange, onPageSizeChange],
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryState(query);
      setCurrentPage(1);
      onPageChange?.(1);
    },
    [onPageChange],
  );

  const setSorting = useCallback(
    (newSortBy: string, newSortDirection: 'ASC' | 'DESC') => {
      setSortByState(newSortBy);
      setSortDirectionState(newSortDirection);
      setCurrentPage(1);
      onPageChange?.(1);
    },
    [onPageChange],
  );

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      onPageChange?.(newPage);
    }
  }, [currentPage, hasNextPage, onPageChange]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      onPageChange?.(newPage);
    }
  }, [currentPage, onPageChange]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
    onPageChange?.(1);
  }, [onPageChange]);

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        onPageChange?.(page);
      }
    },
    [totalPages, onPageChange],
  );

  const setPaginationInfo = useCallback(
    (info: { totalCount: number; totalPages: number; currentPage: number }) => {
      setTotalCount(info.totalCount);
      setTotalPages(info.totalPages);
      // Don't override currentPage from user navigation
    },
    [],
  );

  const reset = useCallback(() => {
    setCurrentPage(1);
    setSearchQueryState('');
    setSortByState('name');
    setSortDirectionState('ASC');
    setHasNextPage(false);
    setTotalCount(0);
    setTotalPages(0);
  }, []);

  const getCurrentOffset = useCallback(() => {
    return (currentPage - 1) * pageSize;
  }, [currentPage, pageSize]);

  const paginationState: PaginationState = useMemo(() => {
    const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endItem = Math.min(currentPage * pageSize, totalCount);

    return {
      pageSize,
      currentPage,
      hasNextPage,
      hasPreviousPage: currentPage > 1,
      totalItems: totalCount,
      startItem,
      endItem,
    };
  }, [pageSize, currentPage, hasNextPage, totalCount]);

  return {
    pageSize,
    currentPage,
    searchQuery,
    sortBy,
    sortDirection,

    setPageSize,
    setSearchQuery,
    setSorting,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToPage,
    reset,

    paginationState,

    getCurrentOffset,
    setHasNextPage,
    setPaginationInfo,
  };
};
